import { SliceState as SliceStateType, sliceSelectors } from '@slice';
import dayjs from 'dayjs';
import { GadmL0ResponseRow } from './gadm';
import { GetCampaignResponse } from './campaignCreator';
import { GetLocationSort } from './location';
import { FeatureWithNullableGeometry } from './tacklebox';
export interface SaveStateRequest {
  slice: SliceStateType;
}

export interface SaveLocationNoteRequest {
  note: string;
  polygon_id: string | number;
}

export interface SaveEntityNoteRequest {
  note: string;
  entity_id: string | number;
}

export interface User {
  email: string;
  created_at?: string;
  updated_at?: string;
  last_active_at?: string;
}

export type SavedStateObject = ReturnType<typeof sliceSelectors.saveStateSelector>;
export interface SavedState {
  state_hash: string;
  state: SavedStateObject;
  name: string;
  created_at: string;
  times_retreived: number;
}

export enum NotificationType {
  CAMPAIGN_INVITE = 'campaign_invite',
  CAMPAIGN_ACCESS = 'campaign_access',
  TACKLEBOX = 'tacklebox',
  CAMPAIGN = 'campaign',
  VIEW = 'view',
  TACKLEBOX_SHARE = 'tacklebox_share'
}

export interface UserNotification {
  id: string;
  user_id: string;
  message: string;
  type: NotificationType;
  seen: boolean;
  data: unknown;
  created_at: Date;
  created_by: string;
}
export interface SummaryEntry {
  date: string; // ISO date string
  count: number;
}

export interface CampaignSummary {
  summary: SummaryEntry[];
  campaign_id: string; // UUID
  job_id: string; // UUID
}
export interface TackleboxShareLink {
  tacklebox_id: string; // UUID
  name: string;
  shared_by: string;
}

export interface TackleboxJobShare {
  user_email: string;
  shared_by: string;
  created_at: string;
}

export interface TackleboxLink {
  tacklebox_id: string; // UUID
  tito_id: string; // UUID
}

export interface GetLocationsResponse {
  polygon_id: string;
  name: string | null;
  translated_name: string | null;
  category: string;
  geometry_wkt: string;
  centroid: [number, number];
  num_entities: string;
  hasc?: string;
  hasc_names?: string;
  area: number;
  has_unit_info?: boolean;
}

export interface GetLocationsResponseWithMetadata {
  data: GetLocationsResponse[];
  metadata: {
    totalCount: number;
    returnedCount: number;
  };
}

export interface ExpandPolygon {
  polygon_id: string;
  polygonCount: number;
}

export interface GetLocationsRequest {
  categories?: string[];
  advertiser_ids?: string[];
  bounding_box?: [[number, number], [number, number]];
  countries?: GadmL0ResponseRow[];
  polygon_ids?: string[];
  sort?: GetLocationSort;
  order?: 'asc' | 'desc';
  min_unique_entities?: number;
  name?: string;
  campaign?: GetCampaignResponse | null;
  mapZoom?: number;
}

export interface GetLocationUnitsResponse {
  polygon_id: string;
  unit_names: string[];
  unit_ids: string[];
  description: string | null;
  is_us: boolean | null;
  source_links: string[];
  parent_unit: string | null;
  locked: string | null;
  locked_by: string | null;
}

export interface UpdateUnitInfoRequest {
  unit_names?: string[];
  unit_ids?: string[];
  description?: string;
  is_us?: boolean;
  source_links?: string[];
  parent_unit?: string;
}

export interface LocationBookmark {
  id: string;
  bookmark_name: string;
  created_at: Date;
  created_by: string;
  polygon_ids: string[];
}

export interface SaveLocationBookmarkRequest {
  bookmark_name: string;
  polygon_ids: string[];
  project_id: string;
}

export interface LocationDetailsDailyStat {
  date_year: number;
  date_month: number;
  date_day: number;
  sum_hourly_visits: number;
  unique_visitors: number;
  sum_dwell_time: number;
  sum_fac_count: number;
  sum_fac_dwell_count: number;
}
export interface LocationDetails {
  daily_stats: LocationDetailsDailyStat[];
}

export interface LocationVisitorsDailyRequest {
  polygon_id: string | number;
  date_range?: [number, number];
}
export interface LocationDailyVisitsResponse {
  date: string;
  unique_visitors: string; // bigint
}

export interface AudienceBookmark {
  id: string;
  bookmark_name: string;
  created_at: Date;
  created_by: string;
  advertiser_ids: string[];
}

export interface SaveAudienceBookmarkRequest {
  bookmark_name: string;
  advertiser_ids: string[];
  project_id: string;
}

export interface AutoCompleteLatLon {
  lat: number;
  lon: number;
  id: string;
  value: string;
}

export interface AutoCompleteMGRS {
  id: string;
  value: string;
  minx: number;
  miny: number;
  maxx: number;
  maxy: number;
}

export interface CreateNewAlertRequest {
  notify_mailbox: boolean;
  notify_email?: boolean;

  target_type: AlertDataEntityTypes;
  target_value: null | string;

  origin_type: AlertDataOriginTypes;
  origin_value: null | string;

  destination_type: AlertDataDestinationType;
  destination_value: null | string;

  name: string;
  project_id: string;
}

export type AlertDataDestinationType = 'activity_inside_location' | 'activity_inside_country' | 'activity_outside_country';
export type AlertDataEntityTypes = 'any_entity' | 'specific_entities';
export type AlertDataOriginTypes = 'any_location' | 'specific_locations';

export interface GADMCountry {
  name: string;
  country_code: string;
}

export interface GetEntityDwellForLocationsRequest {
  entity_id: string;
  polygon_id: string;
  date_range?: [number, number];
}
export interface GetEntityDwellForLocationsResponse {
  date: string;
  count: string;
  dwell_time: number;
  score: number;
}

export interface GetEntityScoresResponse extends GetLocationsResponse {
  pred1: number;
  iso3?: string;
}

export interface GetEntityScoresRequest {
  entity_id: string;
  polygon_ids?: string[];
  campaign_id?: string;
}
export interface GetCampaignTimelineResponse {
  count: string;
  record_date: string;
}
export interface GetCampaignEntityTimelineResponse {
  visit_date: string;
  fac_count_sum: string;
}
export interface GetCampaignEntityTimelineResponseExt extends GetCampaignEntityTimelineResponse {
  epoch: number;
  density: number;
  date: string;
  dayjs: dayjs.Dayjs;
}
export interface GetCampaignTimelineRequest {
  campaign_id: string;
  entity_ids?: string[];
}

export interface RawAudienceRequest {
  entity_ids: string[];
}

export interface GetRawAudienceResponse {
  advertiser_id: string;
  lat: number;
  lon: number;
  record_created_epoch: number;
}

export type RequestTracking = {
  ac: AbortController;
  uuid: string;
};

export interface TitoRequest {
  name: string;
  features: FeatureWithNullableGeometry[];
  minPolygons: number;
}

export interface GetTackleboxHistoryResponse {
  id: string;
  name: string;
  rowboat_job_id: string;
  status: string;
  tool_name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  row_count: number;
}

export interface GetTackleboxHistoryPageResponse {
  data: GetTackleboxHistoryResponse[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EGEH3CachePBFResponseRow {
  count: number;
  index: string;
  epochms?: number; // start of the UTC day; only set for by_day requests
}

export interface EGEH3CachePBFResponse {
  rows: EGEH3CachePBFResponseRow[];
}

export interface EGECacheTimelineRow {
  epochms: number; // start of the UTC day
  count: number;
}

export interface EGEH3CacheResponseRow {
  h3_index: string;
  h3_count: number;
}

export interface EgeCacheClusterH3Row {
  latitude: number;
  longitude: number;
  advertiser_id_count: number;
  advertiser_ids: string[];
  minhour: number;
  maxhour: number;
  hourlist: number[];
  hourcount: number;
  theday: string;
  h3_index: string;
}

export interface EgeCacheClusterH3Response {
  rows: EgeCacheClusterH3Row[];
}

export interface EgeCacheClusterH3PBFRow {
  latitude: number;
  longitude: number;
  advertiserIdCount: number;
  advertiserIds: string[];
  minhour: number;
  maxhour: number;
  hourlist: number[];
  hourcount: number;
  theday: string;
  h3Index: string;
}

export interface EgeCacheClusterH3PBFResponse {
  rows: EgeCacheClusterH3PBFRow[];
}

export interface EgeCacheClusterH3SummarizedRow {
  advertiser_ids: string[];
  advertiser_id_count: number;
  thedays: string[];
  theepochs: number[];
  h3_index: string;
}

export interface EgeCacheClusterH3SummarizedResponse {
  rows: EgeCacheClusterH3SummarizedRow[];
}

export interface EgeCacheDwellH3Row {
  recordCreatedDate: string;
  h3Index: string;
  countSum: number;
  // time_diff_max: number;
  // time_diff_mean: number;
  unique_entities: number[];
}
export interface EgeCacheDwellH3Response {
  rows: EgeCacheDwellH3Row[];
}

export interface EgeCacheDwellH3RowE extends EgeCacheDwellH3Row {
  epoch: number;
}

export interface EgeCacheDwellH3ResponseE {
  rows: EgeCacheDwellH3RowE[];
}
export interface EgeCacheDwellH3RowPBF {
  recordCreatedDate: string;
  h3Index: string;
  countSum: number;
  timeDiffMax: number;
  timeDiffMean: number;
  uniqueEntities: number[];
}
export interface EgeCacheDwellH3RowPBFExt extends EgeCacheDwellH3RowPBF {
  epoch: number;
}
export interface EgeCacheDwellH3RowPBFExtAgg extends EgeCacheDwellH3RowPBFExt {
  agg_count: number;
}
export interface EgeCacheDwellH3ResponsePBF {
  rows: EgeCacheDwellH3RowPBF[];
}

export interface EgeCacheClusterH3SummarizedPBFRow {
  advertiserIds: string[];
  advertiserIdCount: number;
  thedays: string[];
  h3Index: string;
}

export interface EgeCacheClusterH3SummarizedPBFRowExt {
  advertiserIds: string[];
  advertiserIdCount: number;
  thedays: string[];
  h3Index: string;
  theepochs: number[];
}

export interface EgeCacheClusterH3SummarizedPBFResponse {
  rows: EgeCacheClusterH3SummarizedPBFRow[];
}

export interface EGESlimCachePBFResponseRow {
  latitude: number;
  longitude: number;
  recordCreatedTime: number;
  advertiserId: string;
  dataSource: string;
  horizontalAccuracy: number; // whole metres, 0 when the point has none
  verticalAccuracy: number; // whole metres, 0 when the point has none
}

export interface EGESlimCachePBFResponse {
  rows: EGESlimCachePBFResponseRow[];
}

// Enum for dimension labels (what user selects)
export enum CampaignDwellAggDims {
  COUNT = 'Count',
  AVG_DURATION = 'Avg Duration',
  MAX_DURATION = 'Max Duration',
  DAY_PERSISTENCE = 'Day Persistence',
  UNIQUE_ENTITIES = 'Unique Entities'
}

// Keys into your row data (what maps to DB or internal structures)
export enum CampaignDwellAggDimKeys {
  COUNT = 'countSum',
  AVG_DURATION = 'timeDiffMean',
  MAX_DURATION = 'timeDiffMax',
  DAY_PERSISTENCE = 'agg_count',
  UNIQUE_ENTITIES = 'uniqueEntities'
}

// Map dimension label to row key
export const CampaignDwellAggDimToKey: Record<CampaignDwellAggDims, CampaignDwellAggDimKeys> = {
  [CampaignDwellAggDims.COUNT]: CampaignDwellAggDimKeys.COUNT,
  [CampaignDwellAggDims.AVG_DURATION]: CampaignDwellAggDimKeys.AVG_DURATION,
  [CampaignDwellAggDims.MAX_DURATION]: CampaignDwellAggDimKeys.MAX_DURATION,
  [CampaignDwellAggDims.DAY_PERSISTENCE]: CampaignDwellAggDimKeys.DAY_PERSISTENCE,
  [CampaignDwellAggDims.UNIQUE_ENTITIES]: CampaignDwellAggDimKeys.UNIQUE_ENTITIES
};

// Optional: for reverse lookups (not always needed)
export const CampaignDwellKeyToDim = Object.fromEntries(Object.entries(CampaignDwellAggDimToKey).map(([label, key]) => [key, label])) as Record<
  CampaignDwellAggDimKeys,
  CampaignDwellAggDims
>;

export interface WIFIRow {
  observation_type_connected: number;
  observation_type_surveyed: number;
  first_seen_time: number;
  latitude: number;
  longitude: number;
  cluster_id: string;
  wifi_bssid: string;
  wifi_ssid: string;
  classification: string;
  confidence_category: string;
  iso3: string;
}

export interface WifiDetailData {
  cluster_id: string;
  wifi_bssid: string;
  wifi_ssid: string;
  ipv4_address_set: string[];
  ipv6_address_set: string[];
  imei_set: string[];
  imsi_set: string[];
  first_seen_time: string;
  last_seen_time: string;
  longitude: number;
  latitude: number;
  data_source_set: string[];
  advertiser_id_cnts: Record<string, number>;
  device_language_cnts: Record<string, number>;
  confidence_category: string;
  classification: string;
  organization_name: string;
  organization_address: string;
  n_unique_days: number;
  advertiser_id_cnt: number;
  observation_type_connected: number;
  samples_in_cluster: number;
  observation_type_surveyed: number;
}

export interface WifiDetailResponse {
  data: WifiDetailData[];
  metadata: {
    bounds: number[];
  };
}

export interface GetWIFIObservationDetailsResponse {
  advertiser_id: string;
  first_seen_date: string;
  last_seen_date: string;
  num_unique_days: number;
  connected_count: number;
  surveyed_count: number;
  device_language: string[];
}

export interface GetWIFIDetailsRequest {
  cluster_id: string;
  wifi_bssid: string;
  bounds_wkt?: string;
}

export interface GetWIFIObservationDetailsRequest {
  cluster_id: string;
  wifi_bssid: string;
  latitude: number;
  longitude: number;
  min_date: string;
  max_date: string;
}

export interface GetWifiObservationsSurveyedOrConnectedRequest {
  bounds_wkt: string;
  wifi_bssid: string;
  cluster_id: string;
  observation_type?: string;
}

export interface GetWifiObservationsSurveyedOrConnectedResponse {
  advertiser_id: string;
  classification: string;
  data_source: string;
  latitude: number;
  longitude: number;
  observation_type: string;
  record_created_time: string;
  wifi_bssid: string;
  wifi_ssid: string;
}

export interface GeonamesResult {
  name: string;
  country_code: string;
  distance: number;
}

export interface SavedView {
  id: string;
  user_id: string;
  state: string;
  created_at: string;
  last_access?: string;
  name: string;
  description?: string;
  location?: string;
  campaign_id?: string;
  created_by: string;
}

export interface View {
  state: string;
  name: string;
  description?: string;
  location?: string;
  campaign_id?: string;

  // sharing:
  recipient?: string;
  recipient_message?: string;
}

export interface ViewPermission {
  view_id: string;
  invited_by: string;
}

export interface CheckAdvertiserPresenceRequest {
  advertiser_ids: string[];
}

export interface CheckAdvertiserPresenceResponse {
  touchstone: number;
  unknown: number;
}

export interface InviteUserRequest {
  email: string;
  group?: string;
  role?: string;
}

export interface InviteUserResponse {
  email: string;
  password?: string;
  created_in_auth0: boolean;
}

export interface MetaSearchResultLocation {
  polygon_id: string;
  name: string;
  bounds: [number, number, number, number];
  type: 'location';
}
export interface MetaSearchResultEntity {
  entity_id: string;
  bounds: [number, number, number, number];
  type: 'entity';
}
export interface MetaSearchResultPlace {
  name: string;
  bounds: [number, number, number, number];
  type: 'place';
  country_code: string;
}
export interface MetaSearchResultEntityNote {
  entity_id: string;
  bounds: [number, number, number, number];
  note: string;
  type: 'entity-note';
}
export interface MetaSearchResultLocationNote {
  polygon_id: string;
  bounds: [number, number, number, number];
  note: string;
  type: 'location-note';
}
export interface MetaSearchResultCoordinate {
  name: string;
  bounds: [number, number, number, number];
  type: 'coordinate';
}

export type MetaSearchResultType = 'location' | 'entity' | 'place' | 'entity-note' | 'location-note' | 'coordinate';
export type MetaSearchResult =
  MetaSearchResultLocation | MetaSearchResultEntity | MetaSearchResultPlace | MetaSearchResultLocationNote | MetaSearchResultEntityNote | MetaSearchResultCoordinate;

export type MetaSearchRequest = {
  term: string;
  campaign_id?: string | null;
  bounds?: [number, number, number, number];
  categories?: string[];
};

export interface MetricsSaveRequest {
  session_id: string;
  action_type: string;
  action_subtype?: string;
  action_details?: Record<string, unknown>;
  error?: boolean;
  error_message?: string;
  app_version: string;
}
